import type { SearchParams } from "./api";

/** The native filter parser is shared by the browser and request-owned SSR. */
export function parseSearchRequest(search: string): SearchParams {
  const sp = new URLSearchParams(search);
  const number = (key: string) => sp.get(key) ? parseInt(sp.get(key)!) : undefined;
  const coordinate = (key: string) => sp.has(key) && Number.isFinite(parseFloat(sp.get(key)!))
    ? parseFloat(sp.get(key)!) : undefined;
  const list = (key: string) => sp.getAll(key).length ? sp.getAll(key) : undefined;
  const attribute = sp.get("price_attribute");
  return {
    q: sp.get("q") || undefined, rental_type: sp.get("rental_type") || undefined,
    location: sp.get("location") || undefined, location_country: sp.get("location_country") || undefined,
    price_min: number("price_min"), price_max: number("price_max"),
    price_attribute: attribute === "filter_price_per_day" || attribute === "price_driving" ||
      attribute === "price_event" || attribute === "chauffeured_min_price_hourly_rate" ? attribute : undefined,
    make: sp.get("make") || undefined, car_type: list("car_type"), era: list("era"),
    passengers: number("passengers"), sort: sp.get("sort") || undefined, page: number("page") ?? 1,
    date_from: sp.get("date_from") || undefined, date_to: sp.get("date_to") || undefined,
    features: list("features"), bounds_north: coordinate("bounds_north"),
    bounds_south: coordinate("bounds_south"), bounds_east: coordinate("bounds_east"),
    bounds_west: coordinate("bounds_west"), radius: coordinate("radius"),
  };
}

export function resolveSearchRequest({
  params, useMapBounds, countryAnchor, countryCode, locationAnchor,
}: {
  params: SearchParams; useMapBounds: boolean;
  countryAnchor: { applied: boolean; anchorCountry: string | null };
  countryCode?: string | null; locationAnchor?: { lat: number; lng: number } | null;
}): SearchParams {
  if (useMapBounds) return params;
  if (!params.location) return countryAnchor.applied && countryAnchor.anchorCountry
    ? { ...params, country_code: countryAnchor.anchorCountry } : params;
  if (countryCode) return { ...params, location: undefined, radius: undefined,
    bounds_north: undefined, bounds_south: undefined, bounds_east: undefined, bounds_west: undefined,
    country_code: countryCode };
  return { ...params, radius: params.radius && params.radius > 0 ? params.radius : 50,
    ...(locationAnchor ? { anchor_lat: locationAnchor.lat, anchor_lng: locationAnchor.lng } : {}) };
}

/** Exact API query spelling, including repeated array values and zero bounds. */
export function searchRequestApiPath(params: SearchParams): string {
  const sp = new URLSearchParams();
  for (const key of ["q", "rental_type", "location", "country_code", "price_min", "price_max",
    "price_attribute", "make", "model", "classification", "passengers", "sort", "page",
    "pageSize", "date_from", "date_to"] as const) {
    const value = params[key];
    if (value) sp.set(key, String(value));
  }
  for (const key of ["car_type", "era", "colour_id", "features"] as const)
    params[key]?.forEach(value => sp.append(key, value));
  for (const key of ["bounds_north", "bounds_south", "bounds_east", "bounds_west",
    "radius", "anchor_lat", "anchor_lng"] as const)
    if (params[key] != null) sp.set(key, String(params[key]));
  return `/cars/search?${sp}`;
}
